(function () {
  "use strict";

  var FALLBACK_CURRENCY = "USD";

  /**
   * Splits a rendered money string into prefix / numeric body / suffix.
   * Returns null when there is no number in it at all.
   */
  function shape(text) {
    if (typeof text !== "string") return null;
    var first = text.search(/\d/);
    if (first === -1) return null;
    var last = text.length - 1;
    while (last >= 0 && !/\d/.test(text[last])) last -= 1;
    return {
      prefix: text.slice(0, first),
      body: text.slice(first, last + 1),
      suffix: text.slice(last + 1),
    };
  }

  /**
   * Derives a money format from two Liquid-rendered probes, so JS output is
   * identical to the `money` filter's rather than a reimplementation of it.
   *
   * unitProbe    = {{ 100 | money }}        -> prefix, suffix, decimals, divisor
   * groupedProbe = {{ 123456789 | money }}  -> thousands separator
   *
   * One probe is not enough: "1,234,568" cannot be told apart from a
   * three-decimal fraction, and neither probe alone reveals the divisor
   * (JPY is stored x100 but displays zero decimals; KWD is stored x1000).
   */
  function deriveMoneyFormat(unitProbe, groupedProbe) {
    var unit = shape(unitProbe);
    var grouped = shape(groupedProbe);
    if (!unit || !grouped) return null;

    var unitSeps = unit.body.match(/\D/g) || [];
    if (unitSeps.length > 1) return null;

    var decimalSep = unitSeps[0] || "";
    var intPart = decimalSep ? unit.body.split(decimalSep)[0] : unit.body;
    var decPart = decimalSep ? unit.body.split(decimalSep)[1] || "" : "";
    var decimals = decPart.length;
    if (decimals > 3) return null;

    var rendered = Number(intPart + "." + (decPart || "0"));
    if (!rendered) return null;
    var divisor = Math.round(100 / rendered);
    if (divisor !== 1 && divisor !== 100 && divisor !== 1000) return null;

    var groupedBody = grouped.body;
    if (decimals > 0) groupedBody = groupedBody.slice(0, -(decimals + decimalSep.length));
    var thousandsSep = (groupedBody.match(/\D/g) || [])[0] || "";

    return {
      prefix: unit.prefix,
      suffix: unit.suffix,
      decimals: decimals,
      decimalSep: decimalSep,
      thousandsSep: thousandsSep,
      divisor: divisor,
    };
  }

  /** Formats minor units with a derived format; falls back to Intl if absent. */
  function formatMoney(minorUnits, fmt) {
    if (!fmt) {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: window.customBundleCurrency || FALLBACK_CURRENCY,
      }).format(minorUnits / 100);
    }
    var value = minorUnits / fmt.divisor;
    var sign = value < 0 ? "-" : "";
    var fixed = Math.abs(value).toFixed(fmt.decimals);
    var parts = fixed.split(".");
    var grouped = fmt.thousandsSep
      ? parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, fmt.thousandsSep)
      : parts[0];
    var body = parts[1] ? grouped + fmt.decimalSep + parts[1] : grouped;
    return sign + fmt.prefix + body + fmt.suffix;
  }

  /**
   * Confirm-button state from a variant blob (or null) plus the login-for-price and
   * call-for-pricing flags. Both flags render their own CTA in the price slot and hide the
   * confirm button, so neither is purchasable.
   */
  function bundleButtonState(variant, loginForPrice, callForPricing) {
    if (loginForPrice) return { label: "login", purchasable: false };
    if (callForPricing) return { label: "call", purchasable: false };
    if (!variant) return { label: "unavailable", purchasable: false };
    if (variant.available === false) return { label: "oos", purchasable: false };
    return { label: "available", purchasable: true };
  }

  /** Inventory ceiling for a variant blob. Untracked / continue-selling = unlimited. */
  function limitFromVariant(variant) {
    if (!variant || variant.available === false) return 0;
    if (variant.inventoryManagement == null || variant.inventoryPolicy === "continue") {
      return Infinity;
    }
    return variant.inventoryQuantity > 0 ? variant.inventoryQuantity : 0;
  }

  /** DOM-free staging state. Every threshold/clamp rule lives here. */
  class BundleState {
    constructor(config) {
      var cfg = config || {};
      this.requirement = cfg.requirement || "none";
      this.countBy = cfg.countBy || "units";
      this.minQuantity = cfg.minQuantity || 0;
      this.minTotal = cfg.minTotal || 0;
      this.maxProducts = cfg.maxProducts || 0;
      this.lines = [];
    }

    find(variantId) {
      for (var i = 0; i < this.lines.length; i += 1) {
        if (this.lines[i].variantId === variantId) return this.lines[i];
      }
      return null;
    }

    count() {
      if (this.countBy === "products") return this.lines.length;
      var total = 0;
      for (var i = 0; i < this.lines.length; i += 1) total += this.lines[i].qty;
      return total;
    }

    total() {
      var sum = 0;
      for (var i = 0; i < this.lines.length; i += 1) {
        sum += this.lines[i].price * this.lines[i].qty;
      }
      return sum;
    }

    atMaximum() {
      return this.maxProducts > 0 && this.count() >= this.maxProducts;
    }

    /**
     * How many more units of `variantId` may be staged, bounded by both the
     * inventory limit and any remaining bundle maximum.
     */
    capacityFor(variantId) {
      var existing = this.find(variantId);
      var limit = existing ? existing.limit : Infinity;
      var room = Infinity;
      if (this.maxProducts > 0 && this.countBy === "units") {
        room = this.maxProducts - this.count() + (existing ? existing.qty : 0);
      }
      var headroom = Math.min(limit, room);
      return headroom < 0 ? 0 : headroom;
    }

    add(incoming) {
      if (!incoming || !incoming.variantId) return;
      var limit = incoming.limit === undefined ? Infinity : incoming.limit;
      if (limit <= 0) return;

      var existing = this.find(incoming.variantId);
      // atMaximum() already accounts for countBy, so it covers both "products" and "units".
      if (!existing && this.atMaximum()) return;

      if (existing) {
        this.setQty(incoming.variantId, existing.qty + incoming.qty);
        return;
      }

      var room = Infinity;
      if (this.maxProducts > 0 && this.countBy === "units") {
        room = this.maxProducts - this.count();
      }
      var qty = Math.min(incoming.qty, limit, room);
      if (qty <= 0) return;

      this.lines.push({
        variantId: incoming.variantId,
        qty: qty,
        price: incoming.price,
        limit: limit,
        productId: incoming.productId,
        title: incoming.title,
        sku: incoming.sku,
        image: incoming.image,
      });
    }

    setQty(variantId, qty) {
      var existing = this.find(variantId);
      if (!existing) return;
      if (qty <= 0) {
        this.remove(variantId);
        return;
      }
      existing.qty = Math.min(qty, this.capacityFor(variantId));
      if (existing.qty <= 0) this.remove(variantId);
    }

    remove(variantId) {
      this.lines = this.lines.filter(function (l) {
        return l.variantId !== variantId;
      });
    }

    clear() {
      this.lines = [];
    }

    meetsMinimum() {
      if (!this.lines.length) return false;
      if (this.requirement === "quantity") return this.count() >= this.minQuantity;
      if (this.requirement === "total") return this.total() >= this.minTotal;
      return true;
    }

    /**
     * Bar geometry. Spans 0 -> maximum with a marker at the minimum only when the
     * requirement is a quantity AND a maximum exists; a money requirement against a
     * unit maximum would mix units, so it spans 0 -> minimum instead.
     */
    progress() {
      if (this.requirement === "none") return null;
      var money = this.requirement === "total";
      var value = money ? this.total() : this.count();
      var min = money ? this.minTotal : this.minQuantity;
      var spanToMax = !money && this.maxProducts > 0;
      var max = spanToMax ? this.maxProducts : min;
      var marker = spanToMax ? min : null;
      var ratio = max > 0 ? Math.min(value / max, 1) : 0;
      return { value: value, max: max, marker: marker, ratio: ratio };
    }

    serialize() {
      return this.lines.map(function (l) {
        return {
          variantId: l.variantId,
          qty: l.qty,
          price: l.price,
          limit: l.limit === Infinity ? null : l.limit,
          productId: l.productId,
          title: l.title,
          sku: l.sku,
          image: l.image,
        };
      });
    }

    /**
     * Rehydrates from sessionStorage. Each saved entry already carries price/limit/display
     * fields, so no page-scan re-derivation is needed. `isRenderable(productId)` reports
     * whether the product is still present on the page (has a line template) — an entry
     * for a product that has scrolled out of the grid (or vanished) is dropped. Returns the
     * dropped count so the tray can say so.
     */
    restore(saved, isRenderable) {
      this.lines = [];
      if (!Array.isArray(saved)) return 0;
      var dropped = 0;
      for (var i = 0; i < saved.length; i += 1) {
        var e = saved[i];
        if (!e || !e.variantId || (isRenderable && !isRenderable(e.productId))) {
          dropped += 1;
          continue;
        }
        var before = this.lines.length;
        this.add({
          variantId: e.variantId,
          qty: e.qty,
          price: e.price,
          limit: e.limit == null ? Infinity : e.limit,
          productId: e.productId,
          title: e.title,
          sku: e.sku,
          image: e.image,
        });
        if (this.lines.length === before) dropped += 1;
      }
      return dropped;
    }
  }

  class CustomBundle extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.storageKey = "theme:bundle:" + this.sectionId;
      window.customBundleCurrency = this.dataset.currency;

      this.money = deriveMoneyFormat(
        this.textOf("[data-money-probe-unit]"),
        this.textOf("[data-money-probe-grouped]"),
      );

      // data-min-total is in the shop's major units; scale to minor units with the
      // currency divisor (100 if the format could not be derived) so a "total"
      // requirement matches state.total(), which sums raw variant.price minor units.
      var minTotalDivisor = this.money ? this.money.divisor : 100;
      this.state = new BundleState({
        requirement: this.dataset.requirement,
        countBy: this.dataset.countBy,
        minQuantity: parseInt(this.dataset.minQuantity, 10) || 0,
        minTotal: Math.round((parseFloat(this.dataset.minTotal) || 0) * minTotalDivisor),
        maxProducts: parseInt(this.dataset.maxProducts, 10) || 0,
      });

      this.strings = this.parseJSON(".js-custom-bundle-strings") || {};
      this.locale = this.dataset.locale || undefined;
      // Built once so t() can pick the storefront locale's plural form (few / many /
      // …), not just English one/other. Null when Intl.PluralRules or the locale is
      // unavailable — pluralSuffixes() then degrades to one/other.
      this.pluralRules = null;
      if (typeof Intl !== "undefined" && Intl.PluralRules) {
        try {
          this.pluralRules = new Intl.PluralRules(this.locale);
        } catch (e) {
          this.pluralRules = null;
        }
      }

      this.rows = this.querySelector(".js-custom-bundle-rows");
      this.empty = this.querySelector(".js-custom-bundle-empty");
      this.notice = this.querySelector(".js-custom-bundle-notice");
      this.error = this.querySelector(".js-custom-bundle-error");
      this.reason = this.querySelector(".js-custom-bundle-reason");
      this.live = this.querySelector(".js-custom-bundle-live");
      this.clearButton = this.querySelector(".js-custom-bundle-clear");
      this.bar = this.querySelector(".js-custom-bundle-bar");
      this.progressWrap = this.querySelector(".js-custom-bundle-progress");
      this.totalNode = this.querySelector(".js-custom-bundle-total");
      this.commitButtons = this.querySelectorAll(".js-custom-bundle-commit");

      this.addEventListener("click", this.onClick.bind(this));

      this.trayVisible = true;
      this.barOnScreen = false;
      this.submitting = false;
      this._pickerCache = {};
      this.restore();
      this.render();
      this.observeBar();
      this.observeStuck();

      // Keep the sticky tray clear of the sticky header — but only while the header is
      // actually covering the top of the viewport. The theme exposes no header-height
      // CSS var, so mirror facets.js/sticky-cart.js and read the sticky header's height,
      // publishing it as --custom-bundle-header-offset for the tray's top calc(). The
      // header toggles visibility by class (reveal/hide on scroll), so watch its class
      // rather than scroll; the tray's `top` CSS transition matches the header's slide.
      this.stickyHeader = document.querySelector("sticky-header:not([disabled])");
      this.sectionHeader = document.querySelector(".section-header");
      this.updateHeaderOffset();
      this.headerOffsetHandler = this.updateHeaderOffset.bind(this);
      window.addEventListener("resize", this.headerOffsetHandler);
      if (this.stickyHeader && this.sectionHeader) {
        this.headerObserver = new MutationObserver(this.headerOffsetHandler);
        this.headerObserver.observe(this.sectionHeader, {
          attributes: true,
          attributeFilter: ["class"],
        });
      }
    }

    disconnectedCallback() {
      if (this.headerOffsetHandler) {
        window.removeEventListener("resize", this.headerOffsetHandler);
      }
      if (this.headerObserver) this.headerObserver.disconnect();
      if (this.barObserver) this.barObserver.disconnect();
      if (this.stuckObserver) this.stuckObserver.disconnect();
      if (this.onScreenObserver) this.onScreenObserver.disconnect();
      // Section removed in the editor with its bar pinned: release the sticky cart.
      this.barOnScreen = false;
      this.syncBarSlot();
    }

    updateHeaderOffset() {
      // "Covering" = sticky and not translated off-screen: revealed on scroll-up, or
      // always-on. On scroll-down hide it drops to 0 so the tray hugs top: 2rem again.
      var header = this.sectionHeader;
      var covering =
        this.stickyHeader &&
        header &&
        (header.classList.contains("section-header-sticky") ||
          header.classList.contains("section-header-sticky-always")) &&
        !header.classList.contains("section-header-hidden");
      var offset = covering ? this.stickyHeader.offsetHeight : 0;
      this.style.setProperty("--custom-bundle-header-offset", offset + "px");
    }

    textOf(selector) {
      var node = this.querySelector(selector);
      return node ? node.textContent.trim() : "";
    }

    parseJSON(selector, root) {
      var node = (root || this).querySelector(selector);
      if (!node) return null;
      try {
        return JSON.parse(node.textContent);
      } catch (e) {
        return null;
      }
    }

    /**
     * Looks up a section string and substitutes [count]/[amount]/[title] tokens.
     * Strings arrive already localized from the section's Liquid `| t` filters, so
     * this does no locale resolution — an unknown key yields "". A pluralized entry
     * is an object keyed by CLDR category ({ one, few, many, other, … }); t() picks
     * the form for `count` via the locale's plural rules, falling back to "other".
     * A plain string entry (which may still carry a [count] token) is used as-is.
     */
    t(key, replacements) {
      var replace = replacements || {};
      var entry = this.strings[key];
      var value;
      if (entry !== null && typeof entry === "object") {
        var forms = this.pluralCategories(replace.count);
        for (var i = 0; i < forms.length; i += 1) {
          if (entry[forms[i]] !== undefined) {
            value = entry[forms[i]];
            break;
          }
        }
      } else {
        value = entry;
      }
      if (value === undefined) return "";
      Object.keys(replace).forEach(function (token) {
        value = value.split("[" + token + "]").join(String(replace[token]));
      });
      return value;
    }

    /**
     * CLDR plural categories to try for a count, most-specific first with "other"
     * as the universal fallback: Polish 5 -> ["many", "other"], English 2 ->
     * ["other"]. Uses the storefront locale's rules so few / many / two / zero forms
     * select correctly; degrades to one/other when Intl.PluralRules, the locale, or
     * the count is unavailable.
     */
    pluralCategories(count) {
      var category = count === 1 ? "one" : "other";
      if (this.pluralRules && count !== undefined) {
        try {
          category = this.pluralRules.select(count);
        } catch (e) {
          category = count === 1 ? "one" : "other";
        }
      }
      return category === "other" ? ["other"] : [category, "other"];
    }

    cards() {
      // The product grid is server-rendered once, so cache the list — render() walks it on
      // every add/remove/qty change.
      if (!this._cards) {
        this._cards = Array.prototype.slice.call(this.querySelectorAll(".card-product"));
      }
      return this._cards;
    }

    onClick(event) {
      var add = event.target.closest(".js-add-to-bundle");
      if (add) {
        // Multi-variant triggers open the quickview drawer (via drawer-toggle); they carry
        // no inline blob and must not stage on a bare click.
        if (add.classList.contains("js-add-to-bundle-trigger")) return;
        var card = add.closest(".card-product");
        this.stageVariant(card, this.parseJSON(".custom-bundle-variant-json", card), add);
        return;
      }

      var commit = event.target.closest(".js-custom-bundle-commit");
      if (commit) return this.commit();

      var clear = event.target.closest(".js-custom-bundle-clear");
      if (clear) {
        this.state.clear();
        this.persist();
        this.render();
        return;
      }

      var row = event.target.closest(".js-custom-bundle-row");
      if (!row) return;
      var variantId = parseInt(row.dataset.variantId, 10);
      var line = this.state.find(variantId);
      if (!line) return;

      if (event.target.closest(".js-row-remove")) this.state.remove(variantId);
      else if (event.target.closest(".js-row-increase")) this.state.setQty(variantId, line.qty + 1);
      else if (event.target.closest(".js-row-decrease")) this.state.setQty(variantId, line.qty - 1);
      else return;

      this.persist();
      this.render();
    }

    syncCard(card) {
      if (!card) return;
      var trigger = card.querySelector(".js-add-to-bundle");
      if (!trigger) return;
      // Multi-variant triggers open the quickview drawer and carry no inline single-variant
      // blob; gate them only on the bundle maximum.
      if (trigger.classList.contains("js-add-to-bundle-trigger")) {
        trigger.disabled = this.state.atMaximum();
        return;
      }
      // The single-variant card's blob is server-rendered once; parse it per card only once.
      if (!("_variant" in card)) {
        card._variant = this.parseJSON(".custom-bundle-variant-json", card);
      }
      this.applyVariantState(trigger, card._variant, false, false);
    }

    bundleFragmentUrl(productUrl, optionValues) {
      var base = productUrl.split("?")[0];
      var url = base + "?section_id=main-product-do-not-use";
      if (optionValues && optionValues.length) url += "&option_values=" + optionValues.join(",");
      return url;
    }

    /**
     * Resolves one variant for the bundle quickview drawer: fetches (or serves
     * cached) the main-product-do-not-use fragment and returns just the staging
     * blob + login/call flags. It injects no markup — the drawer shows the real
     * product page's variant picker; this fragment is only the data source.
     */
    resolveVariant(productUrl, optionValues, signal) {
      var self = this;
      var url = this.bundleFragmentUrl(productUrl, optionValues);
      var cached = this._pickerCache[url];
      var textPromise = cached
        ? Promise.resolve(cached)
        : fetch(url, { signal: signal })
            .then(function (r) {
              return r.text();
            })
            .then(function (html) {
              self._pickerCache[url] = html;
              return html;
            });
      return textPromise.then(function (html) {
        var doc = new DOMParser().parseFromString(html, "text/html");
        var wrap = doc.querySelector(".custom-bundle-variant-picker");
        if (!wrap) return { variant: null, loginForPrice: false, callForPricing: false };
        return {
          variant: self.parseJSON(".custom-bundle-variant-json", doc),
          loginForPrice: wrap.dataset.loginForPrice === "true",
          callForPricing: wrap.dataset.callForPricing === "true",
        };
      });
    }

    applyButtonState(button, label, enabled) {
      if (!button) return;
      // login / call render their own CTA in the price slot, so hide the confirm button.
      button.hidden = label === "login" || label === "call";
      button.disabled = !enabled;
      var map = {
        available: ".js-add-to-bundle-label",
        oos: ".js-add-to-bundle-oos",
        unavailable: ".js-add-to-bundle-unavailable",
      };
      Object.keys(map).forEach(function (key) {
        var span = button.querySelector(map[key]);
        if (span) span.hidden = key !== label;
      });
    }

    /**
     * Sets a confirm/add button's label and enabled state from a variant blob, folding in
     * the login/call CTAs, the bundle maximum, and the line's remaining stock. Shared by the
     * single-variant card (syncCard) and the quickview drawer CTA (syncBundleCta).
     */
    applyVariantState(button, variant, loginForPrice, callForPricing) {
      var st = bundleButtonState(variant, loginForPrice, callForPricing);
      var atStockLimit = false;
      if (variant) {
        var line = this.state.find(variant.id);
        var stagedQty = line ? line.qty : 0;
        if (stagedQty >= limitFromVariant(variant)) atStockLimit = true;
      }
      this.applyButtonState(
        button,
        st.label,
        st.purchasable && !this.state.atMaximum() && !atStockLimit,
      );
    }

    stageVariant(card, variant, button) {
      if (!variant || variant.available === false) return;
      var before = this.state.count();
      this.state.add({
        variantId: variant.id,
        qty: 1,
        price: variant.price,
        limit: limitFromVariant(variant),
        productId: card.dataset.productId,
        title: variant.title,
        sku: variant.sku,
        image: variant.image,
      });
      this.persist();
      this.render();
      if (this.state.count() > before) {
        if (button) this.confirm(button);
        this.announce(card);
      }
    }

    confirm(button) {
      var label = button.querySelector(".js-add-to-bundle-label");
      var added = button.querySelector(".js-add-to-bundle-added");
      if (!label || !added) return;
      label.hidden = true;
      added.hidden = false;
      clearTimeout(button.confirmTimer);
      button.confirmTimer = setTimeout(function () {
        label.hidden = false;
        added.hidden = true;
      }, 1500);
    }

    announce(card) {
      if (!this.live) return;
      var titleNode = card.querySelector(".card-heading a, .card-title a");
      var title = titleNode ? titleNode.textContent.trim() : "";
      this.live.textContent = this.t("addedAnnouncement", {
        title: title,
        count: this.state.count(),
      });
    }

    restore() {
      var saved = null;
      try {
        saved = JSON.parse(sessionStorage.getItem(this.storageKey));
      } catch (e) {
        saved = null;
      }
      var self = this;
      var dropped = this.state.restore(saved, function (productId) {
        return !!self.querySelector(
          '.js-custom-bundle-line-template[data-product-id="' + productId + '"]',
        );
      });
      if (dropped > 0 && this.notice) {
        this.notice.hidden = false;
        this.notice.textContent = this.t("itemsRemoved", { count: dropped });
      }
      this.persist();
    }

    persist() {
      try {
        sessionStorage.setItem(this.storageKey, JSON.stringify(this.state.serialize()));
      } catch (e) {
        /* private mode / quota — staging still works for this page view */
      }
    }

    format(minorUnits) {
      return formatMoney(minorUnits, this.money);
    }

    /** Publish a pubsub event, but only where pubsub exists (absent in the DOM-free tests). */
    emit(event, data) {
      if (typeof publish === "function" && typeof PUB_SUB_EVENTS !== "undefined") {
        publish(PUB_SUB_EVENTS[event], data);
      }
    }

    render() {
      this.renderRows();
      this.renderProgress();
      this.renderTotal();
      this.renderCommit();
      this.renderBar();
      var self = this;
      this.cards().forEach(function (card) {
        self.syncCard(card);
      });
    }

    // Reconcile the tray rows against state.lines in place: reuse each existing
    // row and update only its mutable fields. Rebuilding from scratch on every
    // qty change would destroy the focused +/- button and drop keyboard /
    // screen-reader focus to <body> mid-interaction.
    renderRows() {
      if (!this.rows) return;
      var self = this;
      var live = {};
      this.state.lines.forEach(function (line) {
        live[line.variantId] = true;
        var row = self.rows.querySelector(
          '.js-custom-bundle-row[data-variant-id="' + line.variantId + '"]',
        );
        if (!row) {
          row = self.buildRow(line);
          if (!row) return;
          self.rows.appendChild(row);
        }
        // A prior commit error may have flagged this row; a fresh render clears
        // it, matching the old rebuild-from-scratch behavior.
        row.classList.remove("custom-bundle-row-error");
        self.setText(row, ".js-row-qty", String(line.qty));
        self.setText(row, ".js-row-price", self.format(line.price * line.qty));
        // Disable + when the line cannot grow — at its inventory limit or the
        // bundle maximum. capacityFor() folds in both.
        var inc = row.querySelector(".js-row-increase");
        if (inc) inc.disabled = self.state.capacityFor(line.variantId) <= line.qty;
      });

      // Drop rows whose line is gone (removed or cleared).
      var existing = this.rows.querySelectorAll(".js-custom-bundle-row");
      for (var i = 0; i < existing.length; i += 1) {
        if (!live[parseInt(existing[i].dataset.variantId, 10)]) existing[i].remove();
      }

      if (this.empty) this.empty.hidden = this.state.lines.length > 0;
      if (this.clearButton) this.clearButton.hidden = this.state.lines.length === 0;
    }

    // Clones a fresh tray row from its product's line template and fills the
    // per-variant content that never changes while the line is staged (title,
    // sku, image). renderRows() owns the mutable qty / price / disabled fields.
    buildRow(line) {
      var tpl = this.querySelector(
        '.js-custom-bundle-line-template[data-product-id="' + line.productId + '"]',
      );
      if (!tpl) return null;
      var row = tpl.content.firstElementChild.cloneNode(true);
      row.dataset.variantId = line.variantId;
      this.setText(row, ".js-row-variant", line.title || "");
      this.setText(row, ".js-row-sku", line.sku || "");
      var img = row.querySelector(".js-row-image");
      if (img) {
        if (line.image) {
          img.src = line.image;
        } else {
          // Imageless product: drop the thumbnail and let the info span its column.
          img.remove();
          row.classList.add("custom-bundle-row-no-image");
        }
      }
      return row;
    }

    setText(root, selector, text) {
      var node = root.querySelector(selector);
      if (node) node.textContent = text;
    }

    renderProgress() {
      var wrap = this.progressWrap;
      if (!wrap) return;
      var p = this.state.progress();
      if (!p) {
        wrap.hidden = true;
        return;
      }
      var fill = wrap.querySelector(".js-custom-bundle-progress-fill");
      var marker = wrap.querySelector(".js-custom-bundle-progress-marker");
      var track = wrap.querySelector('[role="progressbar"]');
      if (fill) fill.style.width = p.ratio * 100 + "%";
      if (track) track.setAttribute("aria-valuenow", String(Math.round(p.ratio * 100)));
      if (marker) {
        if (p.marker === null || !p.max) {
          marker.hidden = true;
        } else {
          marker.hidden = false;
          marker.style.left = (p.marker / p.max) * 100 + "%";
        }
      }
    }

    renderTotal() {
      var node = this.totalNode;
      if (node) node.textContent = this.format(this.state.total());
    }

    renderCommit() {
      var ready = this.state.meetsMinimum();
      var buttons = this.commitButtons;
      for (var i = 0; i < buttons.length; i += 1) buttons[i].disabled = !ready || this.submitting;
      if (!this.reason) return;

      if (ready) {
        this.reason.textContent = this.state.atMaximum() ? this.t("full") : this.t("ready");
        return;
      }
      if (!this.state.lines.length) {
        this.reason.textContent = this.t("reasonEmpty");
        return;
      }

      var p = this.state.progress();
      if (!p) {
        this.reason.textContent = "";
        return;
      }
      if (this.state.requirement === "total") {
        this.reason.textContent = this.t("reasonTotal", { amount: this.format(p.max - p.value) });
        return;
      }
      // Parenthesised: `"x " + a || b - c` would evaluate left-to-right and yield NaN.
      var remaining = (p.marker || p.max) - p.value;
      var key = this.state.countBy === "products" ? "reasonProducts" : "reasonQuantity";
      this.reason.textContent = this.t(key, { count: remaining });
    }

    renderBar() {
      if (!this.bar) return;
      var count = this.state.count();
      this.setText(this.bar, ".js-custom-bundle-bar-count", this.t("barCount", { count: count }));
      this.setText(this.bar, ".js-custom-bundle-bar-total", this.format(this.state.total()));
      // Owns bar.hidden so a stage/remove that changes lines without an intersection
      // change still updates it. trayVisible starts true so the bar is hidden on first
      // paint; the observer flips it as the tray scrolls in/out of view.
      var hidden = this.trayVisible || this.state.lines.length === 0;
      if (hidden === this.bar.hidden) return;
      this.bar.hidden = hidden;
      this.syncBarSlot();
    }

    /**
     * Marks the bar while it holds the slot above the mobile dock — on screen and still
     * pinned there, not settled into the flow at the end of the section. sticky-cart.js
     * gives up that slot for exactly that window; the event carries no payload so
     * listeners recount the marked bars, which stays right with several bundle sections
     * on one page.
     */
    syncBarSlot() {
      if (!this.bar) return;
      var pinned = !this.bar.hidden && this.barOnScreen && this.bar.classList.contains("is-stuck");
      if (pinned === this.bar.classList.contains("is-pinned")) return;
      this.bar.classList.toggle("is-pinned", pinned);
      document.dispatchEvent(new CustomEvent("bundle-bar:change"));
    }

    /** Mirrors facets.js's sticky-collection-actions pattern. */
    observeBar() {
      if (!this.bar || !("IntersectionObserver" in window)) return;
      var tray = this.querySelector(".custom-bundle-tray");
      if (!tray) return;
      var self = this;
      this.barObserver = new IntersectionObserver(
        function (entries) {
          self.trayVisible = entries[0].isIntersecting;
          self.renderBar();
        },
        { threshold: 0 },
      );
      this.barObserver.observe(tray);

      this.onScreenObserver = new IntersectionObserver(
        function (entries) {
          self.barOnScreen = entries[0].isIntersecting;
          self.syncBarSlot();
        },
        { threshold: 0 },
      );
      this.onScreenObserver.observe(this.bar);
    }

    /** Shadow only while the bar floats over the grid; flat once it settles at the end. */
    observeStuck() {
      if (!this.bar || !("IntersectionObserver" in window)) return;
      var sentinel = this.querySelector(".custom-bundle-bar-sentinel");
      if (!sentinel) return;
      var self = this;
      this.stuckObserver = new IntersectionObserver(
        function (entries) {
          // Sentinel marks the bar's resting slot: out of view => bar is stuck.
          self.bar.classList.toggle("is-stuck", !entries[0].isIntersecting);
          self.syncBarSlot();
        },
        { threshold: 0 },
      );
      this.stuckObserver.observe(sentinel);
    }

    commit() {
      // Re-entrancy guard: without it a double-click, or the tray + mobile-bar commit
      // buttons, each POST the whole bundle before the first response clears state.
      if (this.submitting) return;
      if (!this.state.meetsMinimum()) return;
      var self = this;
      this.submitting = true;
      this.renderCommit();
      if (this.error) this.error.hidden = true;

      // Mirror product-form.js: let the mini-cart declare which sections to
      // re-render (its getSectionsToRender resolves to header-mini-cart), then hand
      // the whole response to its own renderContents + open. The previous
      // sections: ["mini-cart"] requested a section that does not exist, so the
      // response carried no HTML and the drawer never refreshed or opened.
      var minicart = document.querySelector("mini-cart");
      var sections =
        minicart && typeof minicart.getSectionsToRender === "function"
          ? minicart.getSectionsToRender().map(function (s) {
              return s.section;
            })
          : [];

      var body = {
        items: this.state.lines.map(function (l) {
          return { id: l.variantId, quantity: l.qty };
        }),
        sections: sections,
        sections_url: window.location.pathname,
      };

      fetch(window.routes.cart_add_url, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(body),
      })
        .then(function (response) {
          return response.json().then(function (json) {
            return { ok: response.ok, json: json };
          });
        })
        .then(function (result) {
          self.submitting = false;
          if (!result.ok) {
            self.showError(result.json);
            self.renderCommit();
            return;
          }
          self.state.clear();
          try {
            sessionStorage.removeItem(self.storageKey);
          } catch (e) {
            /* nothing to clean up */
          }
          self.render();
          self.emit("cartItemAdd", { source: "custom-bundle" });
          // renderContents is defined only once <mini-cart> has upgraded (scripts.js);
          // guarding on it keeps the DOM-free test env from touching the cart drawer.
          if (minicart && typeof minicart.renderContents === "function") {
            minicart.renderContents(result.json);
            if (typeof minicart.open === "function") minicart.open();
          }
        })
        .catch(function () {
          self.submitting = false;
          self.showError(null);
          self.renderCommit();
        });
    }

    /** Keeps every staged item; flags the row Shopify named. */
    showError(payload) {
      if (this.error) {
        this.error.hidden = false;
        // Shopify's own description is more specific than ours; prefer it when present.
        this.error.textContent = (payload && payload.description) || this.t("errorGeneric");
      }
      var offendingId = payload && payload.id ? parseInt(payload.id, 10) : null;
      var rows = this.querySelectorAll(".js-custom-bundle-row");
      for (var i = 0; i < rows.length; i += 1) {
        rows[i].classList.toggle(
          "custom-bundle-row-error",
          offendingId !== null && parseInt(rows[i].dataset.variantId, 10) === offendingId,
        );
      }
      this.emit("cartError", { source: "custom-bundle" });
    }
  }

  if (!customElements.get("custom-bundle")) {
    customElements.define("custom-bundle", CustomBundle);
  }

  window.CustomBundleInternals = {
    deriveMoneyFormat: deriveMoneyFormat,
    formatMoney: formatMoney,
    bundleButtonState: bundleButtonState,
    limitFromVariant: limitFromVariant,
    BundleState: BundleState,
  };
})();
